// Margen actualizado: cómo mueven los tramos de margen el coste unitario y la
// inversión en plataforma del goal. Es la misma fórmula que calcula el backend
// para el WIP (beapolo_backend/src/utils/marginForecastUtils.ts); aquí sirve
// para recalcular en vivo mientras se edita, sin esperar al servidor.
//
// El margen se aplica día a día, así que con la inversión cliente fija cada
// punto de margen mueve el coste unitario plataforma en grossUnitCost / 100.
// Se trabaja en deltas sobre los valores del goal: sin tramos, lo actualizado
// coincide exactamente con lo planificado.

import dayjs from "dayjs";

/** Mínimo que necesita el cálculo del goal de la línea. */
export interface GoalFinancials {
  margin: number;
  grossUnitCost: number;
  netUnitCost: number;
  platformInversion: number;
  targetVolume: number;
  objective: string;
}

/** Resumen de los tramos respecto al primer día editable (lo manda el WIP). */
export interface MarginForecastInput {
  totalDays: number;
  frozenDays: number;
  frozenMarginSum: number;
}

export const unitDivisorFor = (objective: string): number =>
  objective === "CPM" || objective === "CPMV" ? 1000 : 1;

/** Coste unitario plataforma si se compra con `margin` en vez del del goal. */
export const platformUnitCostForMargin = (
  goal: GoalFinancials,
  margin: number
): number =>
  goal.netUnitCost + (goal.grossUnitCost * (goal.margin - margin)) / 100;

/** Inversión plataforma de toda la campaña con un margen medio diario dado. */
export const platformInversionForAverageMargin = (
  goal: GoalFinancials,
  averageMargin: number
): number =>
  goal.platformInversion +
  (goal.targetVolume * goal.grossUnitCost * (goal.margin - averageMargin)) /
    100 /
    unitDivisorFor(goal.objective);

/** Margen medio de la campaña si desde el primer día editable se pone `margin`. */
export const averageMarginIfEdited = (
  forecast: MarginForecastInput,
  margin: number
): number =>
  forecast.totalDays > 0
    ? (forecast.frozenMarginSum +
        margin * (forecast.totalDays - forecast.frozenDays)) /
      forecast.totalDays
    : margin;

/**
 * Inverso de los dos anteriores: margen a poner en los días editables para que
 * la inversión plataforma de la campaña sea `platformInversion`.
 * null si no hay días editables o el goal no permite despejarlo.
 */
export const marginForPlatformInversion = (
  goal: GoalFinancials,
  forecast: MarginForecastInput,
  platformInversion: number
): number | null => {
  const editableDays = forecast.totalDays - forecast.frozenDays;
  const volumeFactor =
    (goal.targetVolume * goal.grossUnitCost) /
    100 /
    unitDivisorFor(goal.objective);
  if (editableDays <= 0 || volumeFactor === 0) return null;
  const averageMargin =
    goal.margin - (platformInversion - goal.platformInversion) / volumeFactor;
  return (
    (averageMargin * forecast.totalDays - forecast.frozenMarginSum) /
    editableDays
  );
};

/** Tramos consolidados (startDate/endDate inclusivos) → margen de cada día. */
export const expandTimeline = (
  periods: { startDate: string; endDate: string; margin: number }[]
): Map<string, number> => {
  const daily = new Map<string, number>();
  periods.forEach((p) => {
    let day = dayjs(p.startDate.split("T")[0]);
    const end = dayjs(p.endDate.split("T")[0]);
    while (!day.isAfter(end)) {
      daily.set(day.format("YYYY-MM-DD"), p.margin);
      day = day.add(1, "day");
    }
  });
  return daily;
};

/** Media de los márgenes diarios. null sin días. */
export const averageDailyMargin = (
  daily: Map<string, number>
): number | null => {
  if (daily.size === 0) return null;
  let sum = 0;
  daily.forEach((m) => {
    sum += m;
  });
  return sum / daily.size;
};

/**
 * Resumen para un tramo hipotético [start, end]: los días dentro cambian al
 * margen nuevo y el resto conserva el suyo (el tramo nuevo tapa a los demás,
 * igual que al guardarlo).
 */
export const forecastForRange = (
  daily: Map<string, number>,
  start: string,
  end: string
): MarginForecastInput => {
  let frozenDays = 0;
  let frozenMarginSum = 0;
  daily.forEach((m, day) => {
    if (day < start || day > end) {
      frozenDays += 1;
      frozenMarginSum += m;
    }
  });
  return { totalDays: daily.size, frozenDays, frozenMarginSum };
};
